import { FC, memo, useCallback, useEffect, useState } from 'react';

import { ModalTypes, ModalProps } from 'application/providers/ModalProvider';
import { RouterUtils, useNavigate, useParams } from 'application/providers/RouterProvider';
import { STREAMS_CATEGORY_PATH } from 'application/consts';
import AnalyticsService, {
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { toast } from '@shared/ui';
import { TEXTS as POINTS_TEXTS } from 'application/pages/PointsPage/renderer/texts';

import { UserFeatures } from '@shared/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Tabs from '@zvuk-b2b/react-uikit/ui/Tabs';

import useSelectPlayerModalViewModel from './useSelectPlayerModalViewModel';
import { TEXTS } from './texts';
import { CHANGE_DEVICE_FLOWS, FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS, VIDEO_FLOWS } from '../consts';
import {
  DeviceBindStatus,
  DeviceTypeEventMap,
  DeviceTypesInFlow,
  PlayerTypesInFlow,
  SelectPlayerContentViewMode,
  SelectPlayerFlowType,
} from '../types';
import { PLAYER_TO_DEVICE_TYPE, MODAL_MODE_BY_DEVICE_TYPE } from './consts';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';
import { PlayerTypeItemList } from './PlayerTypeItemList';

import styles from './SelectPlayerModal.module.scss';

export const TAB_ITEMS = [
  {
    id: SelectPlayerContentViewMode.AUDIO_STREAM,
    content: TEXTS.TAB_AUDIO_TITLE,
  },
  {
    id: SelectPlayerContentViewMode.VIDEO_STREAM,
    content: TEXTS.TAB_VIDEO_TITLE,
  },
];

const SelectPlayerModal: FC<ModalProps[ModalTypes.SELECT_PLAYER_MODAL]> = ({ openModal, replaceModal, close }) => {
  const { isTemplateMode, flowType, contentViewMode, currentPoint, selectedPlayerInFlow } = useOnboardingContext();
  const { setCurrentPointDevice, setContentViewMode, setSelectedPlayerInFlow, clearCurrentPoint } =
    useOnboardingActionContext();

  const navigate = useNavigate();
  const { isIOS, isSafariDesktop } = useMedia();
  const pathParams = useParams<{ id?: string }>();
  const targetStreamId = pathParams.id;

  const {
    playersData,
    videoPlayersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isUserFeaturesOnboardingPassLoading,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
  } = useSelectPlayerModalViewModel({
    pointHasVideoSupport: currentPoint?.isVideoMode,
  });

  const [confirmSelectedPlayerLoading, setConfirmSelectedPlayerLoading] = useState<boolean>(false);

  const pointDevice = currentPoint.device?.type;
  const pointDeviceBound = (currentPoint?.device?.bindStatus === DeviceBindStatus.BINDED && pointDevice) || undefined;
  const haveBoundDevice =
    Boolean(flowType && CHANGE_DEVICE_FLOWS.has(flowType)) &&
    (!!currentPoint?.stream?.id || !!currentPoint?.videoStream?.id) &&
    !!pointDeviceBound;

  const isLoading =
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    confirmSelectedPlayerLoading;

  const modalTitle = haveBoundDevice ? TEXTS.MODAL_WARNING_TITLE : TEXTS.MODAL_UPDATED_TITLE;

  useEffect(() => {
    setContentViewMode(
      flowType && VIDEO_FLOWS.has(flowType)
        ? SelectPlayerContentViewMode.VIDEO_STREAM
        : SelectPlayerContentViewMode.AUDIO_STREAM
    );
  }, [flowType]);

  useEffect(() => {
    setSelectedPlayerInFlow(
      flowType && VIDEO_FLOWS.has(flowType) ? PlayerTypesInFlow.MOBILE : PlayerTypesInFlow.WEBPLAYER
    );
  }, [flowType]);

  const onViewModeChange = (value: string | number) => {
    const newViewMode = value as SelectPlayerContentViewMode;
    setSelectedPlayerInFlow(
      newViewMode === SelectPlayerContentViewMode.AUDIO_STREAM ? PlayerTypesInFlow.WEBPLAYER : PlayerTypesInFlow.MOBILE
    );
    setContentViewMode(newViewMode);
  };

  const onPlayerChange = (playerType: PlayerTypesInFlow) => {
    setSelectedPlayerInFlow(playerType);
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_SELECT_PLAYER_RADIO_CLICKED, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: playerType,
    });
  };

  const onBindStream = async () => {
    const streamId = targetStreamId;

    if (isTemplateMode) {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: 'saved',
          collectionId: 0,
          id: streamId!,
        })
      );
    }

    const isOk = await onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    if (!isOk) {
      throw new Error();
    }

    refetchFeaturesOnboarding();
  };

  const handleUpdateClick = async (pointId: string) => {
    try {
      const { ok, deviceType, requiredVersionUrl } = await pointSetDevice({
        pointId,
        deviceBindingCode: undefined,
        createWebPlayer: true,
      });

      if (ok && deviceType) {
        AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_PLAYER_TYPE_BOUND, {
          [StreamBindFlowParamNameAMP.PLAYER_TYPE]: deviceType,
          [PointParamNameAMP.POINT_ID]: pointId,
        });
      }

      return requiredVersionUrl;
    } catch (err) {
      toast.error(POINTS_TEXTS.PLAYER_CHANGE_DEVICE_MODAL_UNKNOWN_ERROR);
    }

    return undefined;
  };

  const onSelectWebPlayer = useCallback(async () => {
    const requiredVersionUrl = await handleUpdateClick(currentPoint?.id || '');

    if (
      currentPoint?.isEnabled === true &&
      requiredVersionUrl &&
      (flowType === SelectPlayerFlowType.BIND_STREAM ||
        flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM)
    ) {
      window.open(requiredVersionUrl, isIOS || isSafariDesktop ? '_top' : '_blank');
      clearCurrentPoint();
      close();

      return;
    }

    setCurrentPointDevice({ requiredVersionUrl, type: DeviceTypesInFlow.WEBPLAYER });

    if (flowType && FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS.has(flowType)) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (
      currentPoint?.isEnabled === false &&
      (flowType === SelectPlayerFlowType.FULL_ONBOARDING ||
        flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM)
    ) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {});
    }
  }, [currentPoint, flowType, setCurrentPointDevice, clearCurrentPoint]);

  const onSelectPlayer = async () => {
    setCurrentPointDevice({ type: PLAYER_TO_DEVICE_TYPE[selectedPlayerInFlow] });
    replaceModal(MODAL_MODE_BY_DEVICE_TYPE[selectedPlayerInFlow], {});
  };

  const handleClick = async () => {
    setConfirmSelectedPlayerLoading(true);

    if (
      selectedPlayerInFlow === PlayerTypesInFlow.WEBPLAYER &&
      (flowType === SelectPlayerFlowType.BIND_STREAM ||
        flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM)
    ) {
      await onBindStream();
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_SELECT_PLAYER_CLICKED, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: selectedPlayerInFlow,
      [StreamBindFlowParamNameAMP.DEVICE_TYPE]: DeviceTypeEventMap[selectedPlayerInFlow],
    });

    if (selectedPlayerInFlow === PlayerTypesInFlow.WEBPLAYER) {
      await onSelectWebPlayer();
    } else {
      await onSelectPlayer();
    }

    setConfirmSelectedPlayerLoading(false);
  };

  const onConfirmBack = () => {
    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {
      afterClose: () => {
        setTimeout(() => {
          switch (flowType) {
            case SelectPlayerFlowType.CREATE_POINT: {
              openModal(ModalTypes.POINT_CREATE, {});
              break;
            }
            case SelectPlayerFlowType.CREATE_DEMO_POINT: {
              openModal(ModalTypes.VIDEO_STREAM_BIND, {
                selectedPoints: [],
                isDenyCreatePoint: false,
              });
              break;
            }
            default: {
              openModal(ModalTypes.STREAM_BIND, {});
            }
          }
        }, 0);
      },
    });
  };

  const onStop = () => {
    if (flowType === SelectPlayerFlowType.CREATE_POINT || flowType === SelectPlayerFlowType.CREATE_DEMO_POINT) {
      onConfirmBack();

      return;
    }

    if (flowType && CHANGE_DEVICE_FLOWS.has(flowType)) {
      close();
      clearCurrentPoint();
    } else {
      onConfirmBack();
    }
  };

  const onClose = () => {
    if (flowType === SelectPlayerFlowType.CREATE_POINT || flowType === SelectPlayerFlowType.CREATE_DEMO_POINT) {
      openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});

      return;
    }

    if (flowType && CHANGE_DEVICE_FLOWS.has(flowType)) {
      close();
      clearCurrentPoint();
    } else {
      openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
    }
  };

  return (
    <ModalScreen
      title={modalTitle}
      titlePlacement="left"
      onClose={onClose}
      containerClassName={styles.modalContainer}
      containerActionButtonsClassName={styles.actionButtons}
      actionButtons={[
        <Button
          key="button_select_player_success"
          elementId="button_select_player_success"
          type={ButtonType.PRIMARY}
          isLoading={isLoading}
          onClick={handleClick}>
          {haveBoundDevice ? TEXTS.SUCCESS_CHANGE_BUTTON : TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_select_player_cancel"
          elementId="button_select_player_cancel"
          type={ButtonType.SECONDARY}
          isLoading={isLoading}
          onClick={onStop}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      {currentPoint.address && <span>{TEXTS.MODAL_ADDRESS_TITLE(currentPoint.address)}</span>}

      {haveBoundDevice && (
        <Info
          className={styles.warning}
          type={InfoTypes.ERROR}>
          {TEXTS.CHANGE_DEVICE_TYPE_WARNING}
        </Info>
      )}

      {flowType === SelectPlayerFlowType.CHANGE_AUDIO_VIDEO_DEVICE && (
        <Tabs
          items={TAB_ITEMS}
          elementId="tabs_content_type_mode"
          selectedItemId={contentViewMode}
          className={styles.tabs}
          tabItemClassName={styles.tabItem}
          onTabChange={onViewModeChange}
        />
      )}

      <PlayerTypeItemList
        contentViewMode={contentViewMode}
        playersData={playersData}
        videoPlayersData={videoPlayersData}
        selectedPlayerInFlow={selectedPlayerInFlow}
        onPlayerChange={onPlayerChange}
      />
    </ModalScreen>
  );
};

export default memo(SelectPlayerModal);
